import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import { TrendingUp, DollarSign, Sparkles } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompoundCalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState(1000);
  const [monthlyContribution, setMonthlyContribution] = useState(200);
  const [years, setYears] = useState(20);
  const [interestRate, setInterestRate] = useState(7);

  // Load saved progress on mount
  useEffect(() => {
    const loadSavedProgress = async () => {
      const userData = localStorage.getItem("user_data");
      if (!userData) return;
      
      try {
        const user = JSON.parse(userData);
        const userId = user.id || user.email;
        const response = await axios.get(`${API_URL}/api/progress/${userId}`);
        const savedProgress = response.data?.tools_progress?.["compound-calculator"];
        
        if (savedProgress) {
          if (savedProgress.initialInvestment) setInitialInvestment(savedProgress.initialInvestment);
          if (savedProgress.monthlyContribution) setMonthlyContribution(savedProgress.monthlyContribution);
          if (savedProgress.years) setYears(savedProgress.years);
          if (savedProgress.interestRate) setInterestRate(savedProgress.interestRate);
        }
      } catch (e) {
        // Silent fail - no saved progress
      }
    };
    
    loadSavedProgress();
  }, []);

  // Save progress when inputs change (debounced)
  useEffect(() => {
    const saveProgress = async () => {
      const userData = localStorage.getItem("user_data");
      if (!userData) return;
      
      try {
        const user = JSON.parse(userData);
        const userId = user.id || user.email;
        
        await axios.post(`${API_URL}/api/progress/tool`, {
          user_id: userId,
          tool_id: "compound-calculator",
          progress_data: {
            initialInvestment,
            monthlyContribution,
            years,
            interestRate
          }
        });
      } catch (e) {
        // Silent fail
      }
    };

    const timeoutId = setTimeout(saveProgress, 2000); // Debounce 2 seconds
    return () => clearTimeout(timeoutId);
  }, [initialInvestment, monthlyContribution, years, interestRate]);

  const calculateCompoundInterest = (principal, monthly, rate, time) => {
    const annualRate = rate / 100;
    
    // Future value of initial investment
    const principalFV = principal * Math.pow(1 + annualRate, time);
    
    // Future value of monthly contributions (annual compounding approximation)
    let contributionFV = 0;
    for (let i = 0; i < time; i++) {
      contributionFV += monthly * 12 * Math.pow(1 + annualRate, time - i - 1);
    }
    
    return principalFV + contributionFV;
  };

  const generateChartData = useMemo(() => {
    const data = [];
    const step = years > 30 ? 5 : years > 15 ? 2 : 1;
    for (let year = 0; year <= years; year += step) {
      const totalValue = calculateCompoundInterest(
        initialInvestment,
        monthlyContribution,
        interestRate,
        year
      );
      const totalContributions = initialInvestment + monthlyContribution * 12 * year;
      const earnings = totalValue - totalContributions;

      data.push({
        year,
        total: Math.round(totalValue),
        contributions: Math.round(totalContributions),
        earnings: Math.round(earnings),
      });
    }
    // Always include the final year
    if (data[data.length - 1]?.year !== years) {
      const totalValue = calculateCompoundInterest(
        initialInvestment,
        monthlyContribution,
        interestRate,
        years
      );
      const totalContributions = initialInvestment + monthlyContribution * 12 * years;
      data.push({
        year: years,
        total: Math.round(totalValue),
        contributions: Math.round(totalContributions),
        earnings: Math.round(totalValue - totalContributions),
      });
    }
    return data;
  }, [initialInvestment, monthlyContribution, years, interestRate]);

  const finalAmount = generateChartData[generateChartData.length - 1]?.total || 0;
  const totalContributions = initialInvestment + monthlyContribution * 12 * years;
  const totalEarnings = finalAmount - totalContributions;

  const formatCurrency = (value) => {
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(1)}B`;
    }
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatFullCurrency = (value) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const parseInputValue = (value, max) => {
    const num = parseInt(value.replace(/[^0-9]/g, ''), 10);
    if (isNaN(num)) return 0;
    return Math.min(num, max);
  };

  const getPersonalizedMessage = () => {
    if (finalAmount > 100000000) {
      return `Wow! With this strategy, you're looking at ${formatCurrency(finalAmount)} - that's generational wealth territory! Time and compound interest are incredibly powerful.`;
    } else if (finalAmount > 10000000) {
      return `Incredible! ${formatCurrency(finalAmount)} would make you a multi-millionaire. This is the power of starting early and staying consistent.`;
    } else if (finalAmount > 1000000) {
      return `You're on track to become a millionaire with ${formatCurrency(finalAmount)}! The magic of compound interest is real.`;
    } else if (finalAmount > 100000) {
      return `${formatCurrency(finalAmount)} is serious wealth! That could be a house down payment, early retirement fund, or complete financial freedom.`;
    } else {
      return `Every journey starts somewhere! ${formatCurrency(finalAmount)} is a great foundation to build on.`;
    }
  };

  return (
    <Card className="glass shadow-floating border-0" data-testid="compound-calculator">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-500" />
          Your Investment Calculator
        </CardTitle>
        <p className="text-sm text-slate-500">
          See how your money can grow with compound interest
        </p>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Sliders with Input Fields */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Initial Investment */}
          <div className="space-y-3">
            <Label className="flex items-center justify-between">
              <span>Initial Investment</span>
              <Input
                type="text"
                value={formatFullCurrency(initialInvestment)}
                onChange={(e) => setInitialInvestment(parseInputValue(e.target.value, 500000))}
                className="w-32 h-8 text-right font-mono text-emerald-600 font-semibold"
                data-testid="input-initial"
              />
            </Label>
            <Slider
              value={[initialInvestment]}
              onValueChange={(value) => setInitialInvestment(value[0])}
              min={0}
              max={500000}
              step={500}
              className="slider-emerald"
              data-testid="slider-initial"
            />
            <p className="text-xs text-slate-400">$0 - $500,000</p>
          </div>

          {/* Monthly Contribution */}
          <div className="space-y-3">
            <Label className="flex items-center justify-between">
              <span>Monthly Contribution</span>
              <Input
                type="text"
                value={formatFullCurrency(monthlyContribution)}
                onChange={(e) => setMonthlyContribution(parseInputValue(e.target.value, 2000))}
                className="w-32 h-8 text-right font-mono text-emerald-600 font-semibold"
                data-testid="input-monthly"
              />
            </Label>
            <Slider
              value={[monthlyContribution]}
              onValueChange={(value) => setMonthlyContribution(value[0])}
              min={0}
              max={2000}
              step={25}
              className="slider-emerald"
              data-testid="slider-monthly"
            />
            <p className="text-xs text-slate-400">$0 - $2,000/month</p>
          </div>

          {/* Investment Period */}
          <div className="space-y-3">
            <Label className="flex items-center justify-between">
              <span>Investment Period</span>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  value={years}
                  onChange={(e) => setYears(Math.min(Math.max(1, parseInt(e.target.value) || 1), 60))}
                  className="w-20 h-8 text-right font-mono text-emerald-600 font-semibold"
                  min={1}
                  max={60}
                  data-testid="input-years"
                />
                <span className="text-sm text-slate-500">years</span>
              </div>
            </Label>
            <Slider
              value={[years]}
              onValueChange={(value) => setYears(value[0])}
              min={1}
              max={60}
              step={1}
              className="slider-emerald"
              data-testid="slider-years"
            />
            <p className="text-xs text-slate-400">1 - 60 years</p>
          </div>

          {/* Interest Rate */}
          <div className="space-y-3">
            <Label className="flex items-center justify-between">
              <span>Expected Return Rate</span>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Math.min(Math.max(1, parseFloat(e.target.value) || 1), 12))}
                  className="w-20 h-8 text-right font-mono text-emerald-600 font-semibold"
                  min={1}
                  max={12}
                  step={0.5}
                  data-testid="input-rate"
                />
                <span className="text-sm text-slate-500">%</span>
              </div>
            </Label>
            <Slider
              value={[interestRate]}
              onValueChange={(value) => setInterestRate(value[0])}
              min={1}
              max={12}
              step={0.5}
              className="slider-emerald"
              data-testid="slider-rate"
            />
            <p className="text-xs text-slate-400">
              1% - 12% <span className="text-emerald-600 ml-2">7% is the inflation-adjusted market average</span>
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="h-[250px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={generateChartData}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorContributions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0F172A" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#0F172A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="year"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#64748B", fontSize: 12 }}
                tickFormatter={(value) => `Yr ${value}`}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#64748B", fontSize: 12 }}
                tickFormatter={(value) => formatCurrency(value)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "none",
                  borderRadius: "12px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value) => [formatFullCurrency(value)]}
                labelFormatter={(label) => `Year ${label}`}
              />
              <Area
                type="monotone"
                dataKey="contributions"
                stroke="#0F172A"
                strokeWidth={2}
                fill="url(#colorContributions)"
                name="Your Contributions"
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke="#10B981"
                strokeWidth={2}
                fill="url(#colorTotal)"
                name="Total Value"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Results */}
        <div className="grid grid-cols-3 gap-4">
          <motion.div
            key={totalContributions}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-50 rounded-xl p-4 text-center"
          >
            <DollarSign className="w-5 h-5 text-slate-500 mx-auto mb-1" />
            <p className="text-xs text-slate-500 mb-1">You Invest</p>
            <p className="font-mono text-lg font-bold text-navy-700">
              {formatCurrency(totalContributions)}
            </p>
          </motion.div>

          <motion.div
            key={totalEarnings}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-emerald-50 rounded-xl p-4 text-center"
          >
            <TrendingUp className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
            <p className="text-xs text-emerald-600 mb-1">You Earn</p>
            <p className="font-mono text-lg font-bold text-emerald-600">
              {formatCurrency(totalEarnings)}
            </p>
          </motion.div>

          <motion.div
            key={finalAmount}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-center text-white"
          >
            <Sparkles className="w-5 h-5 mx-auto mb-1" />
            <p className="text-xs opacity-80 mb-1">Total Value</p>
            <p className="font-mono text-lg font-bold">
              {formatCurrency(finalAmount)}
            </p>
          </motion.div>
        </div>

        {/* Personalized Message */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-sm text-emerald-800 leading-relaxed">
            {getPersonalizedMessage()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompoundCalculator;
